import {
    useState,
    type FC
} from "react";
import {
    ImageBackground,
    TouchableOpacity,
    Platform,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    Text
} from "ncore-ui-kit";
import type {
    FormWidgetProps
} from "../../type";
import {
    CircleAlert,
    Camera
} from "lucide-react-native";
import ImageCropPicker, {
    type Image as CropImage
} from "react-native-image-crop-picker";


const PhotoWidget: FC<FormWidgetProps> = ({
    onChange,
    formID,
    field,
    value
}) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    // We can display the value directly if it is a URL, or the local path if just uploaded.
    const [
        localUri,
        setLocalUri
    ] = useState<string | null>(null);

    const handleSelectImage = () => {
        if(Platform.OS === "web") {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/*";
            input.onchange = (e: Event) => {
                const target = e.target as HTMLInputElement;
                const file = target.files?.[0];
                if(file) {
                    const objectUrl = URL.createObjectURL(file);
                    setLocalUri(objectUrl);
                    onChange(objectUrl);
                }
            };
            input.click();
            return;
        }

        ImageCropPicker.openPicker({
            cropping: false,
            mediaType: "photo",
            height: 1024,
            width: 1024
        }).then((image) => {
            if(image) {
                uploadImage(image);
            }
        }).catch(err => {
            console.log(err);
        });
    };

    const uploadImage = async (image: CropImage) => {
        if(!formID) {
            NCoreUIKitSnackBar.open({
                icon: ({
                    color
                }) => <CircleAlert
                    color={colors.content.icon[color ? color : "default"]}
                />,
                subTitle: localize("anErrorOccured"),
                title: localize("error"),
                autoCloseDelay: 5000,
                isShowAction: false,
                type: "danger"
            });
            return;
        }

        const path = image.path.startsWith("file://") ? image.path : `${"file://"}${image.path}`;
        setLocalUri(path);
        onChange(path);
    };

    return <View
        style={{
            marginBottom: spaces.spacingMd
        }}
    >
        <Text
            variant="labelMediumSize"
            color="high"
            style={{
                marginBottom: spaces.spacingSm
            }}
        >
            {field.title} {field.isRequired !== false && <Text
                color="danger"
            >
                *
            </Text>}
        </Text>
        <TouchableOpacity
            style={[
                stylesheet.buttonContainer,
                {
                    backgroundColor: colors.content.container.subtle,
                    borderColor: colors.content.border.default,
                    borderWidth: borders.line,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingMd
                }
            ]}
            onPress={handleSelectImage}
        >
            {(localUri || value) ? <ImageBackground
                source={{
                    uri: localUri ? localUri : value
                }}
                style={[
                    stylesheet.imageBackground,
                    {
                        borderRadius: radiuses.sm
                    }
                ]}
                resizeMode="contain"
            >
                <View
                    style={stylesheet.contentContainer}
                >
                    <Camera
                        color={colors.content.icon.default}
                        size={20}
                    />
                    <Text
                        variant="bodyMediumSize"
                        color="high"
                        style={{
                            marginTop: spaces.spacingXs
                        }}
                    >
                        {localUri ? localize("choosePhoto") : localize("uploadPhoto")}
                    </Text>
                </View>
            </ImageBackground> : <View
                style={[
                    stylesheet.emptyContentContainer,
                    {
                        paddingVertical: spaces.spacingXl
                    }
                ]}
            >
                <Camera
                    color={colors.content.icon.default}
                    size={32}
                />
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                    style={{
                        marginTop: spaces.spacingSm
                    }}
                >
                    {localize("uploadPhoto")}
                </Text>
            </View>}
        </TouchableOpacity>
    </View>;
};

export default PhotoWidget;
